import React, { useState } from 'react';
import { Copy, Check, ExternalLink, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

// Enlace publico de una solicitud de firma ya creada. El backend ya devuelve "url" en
// /api/firmas/expediente/{id} y /api/firmas/pendientes; si faltara, se reconstruye con el token.
export const urlDeFirma = (firma) =>
  (firma && firma.url) || (firma && firma.token ? `${window.location.origin}/firmar/${firma.token}` : '');

const copiarAlPortapapeles = async (texto) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch (e) {
    // sin permisos: se intenta el metodo antiguo
  }
  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch (e) {
    return false;
  }
};

// Botones para reenviar el mismo enlace de firma sin tener que borrar ni renovar la peticion.
const CompartirEnlaceFirma = ({ firma, compacto = false }) => {
  const [copiado, setCopiado] = useState(false);
  const url = urlDeFirma(firma);
  if (!firma || !url || firma.estado !== 'pendiente') return null;

  const nombre = firma.firmante_nombre ? ` ${firma.firmante_nombre}` : '';
  const documento = firma.nombre_documento || 'el documento';
  const texto = `Hola${nombre}: aquí tienes el documento "${documento}" para firmar. `
    + `El enlace es personal y caduca: ${url}`;

  const onCopiar = async (e) => {
    e.stopPropagation();
    const ok = await copiarAlPortapapeles(url);
    if (ok) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
      toast({
        title: 'Enlace copiado',
        description: 'Ya lo puedes pegar en WhatsApp, en un email o en un SMS.',
      });
    } else {
      window.prompt('Copia el enlace de firma:', url);
    }
  };

  const onAbrir = (e) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener');
  };

  const onWhatsapp = (e) => {
    e.stopPropagation();
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  };

  const alto = compacto ? 'h-6 px-1.5' : 'h-8';
  const icono = compacto ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <span className="inline-flex items-center gap-1 flex-shrink-0"
          data-testid={`firma-compartir-${firma.id}`}>
      <Button size="sm" variant={compacto ? 'ghost' : 'outline'} className={alto}
              onClick={onCopiar}
              title={`Copiar el enlace de firma para enviarlo por WhatsApp, email o SMS\n${url}`}
              data-testid={`firma-copiar-enlace-${firma.id}`}>
        {copiado
          ? <Check className={`${icono} text-green-600 ${compacto ? '' : 'mr-1'}`} />
          : <Copy className={`${icono} text-indigo-600 ${compacto ? '' : 'mr-1'}`} />}
        {!compacto && (copiado ? 'Copiado' : 'Copiar enlace')}
      </Button>
      <Button size="sm" variant="ghost" className={alto} onClick={onAbrir}
              title="Abrir el enlace de firma en una pestaña nueva"
              data-testid={`firma-abrir-enlace-${firma.id}`}>
        <ExternalLink className={`${icono} text-gray-500`} />
      </Button>
      <Button size="sm" variant="ghost" className={alto} onClick={onWhatsapp}
              title="Enviar el enlace por WhatsApp"
              data-testid={`firma-whatsapp-${firma.id}`}>
        <MessageCircle className={`${icono} text-green-600`} />
      </Button>
    </span>
  );
};

export default CompartirEnlaceFirma;
